/**
 * useDomainContext
 * =================
 * Detecta se a URL atual é um domínio customizado de município
 * (ex: empontenova.com.br) e resolve o municipio_id correspondente
 * via /api/domain-resolve.
 *
 * O hostname é compared contra `MAIN_DOMAINS` antes — para evitar uma
 * chamada de rede no carregamento de qualquer página do domínio principal.
 *
 * Resposta:
 *   {
 *     isCustomDomain: boolean,
 *     municipio: { municipio_id, slug, nome, logo_url, cor_primaria, ... } | null,
 *     loading: boolean,
 *     resolved: boolean
 *   }
 */
import { useState, useEffect } from 'react';

const MAIN_DOMAINS = [
  'empreendecomunidade.com.br',
  'www.empreendecomunidade.com.br',
  'localhost',
  '127.0.0.1',
];

// Permite extender via env (build-time). Útil para staging/preview.
const EXTRA = (process.env.REACT_APP_MAIN_DOMAINS || '')
  .split(',')
  .map((d) => d.trim().toLowerCase())
  .filter(Boolean);

const ALL_MAIN = new Set([...MAIN_DOMAINS, ...EXTRA]);

function isPreviewHost(hostname) {
  // Hostnames de preview do Emergent (sufixo .preview.emergentagent.com)
  // são considerados domínio principal — nunca tentam resolver como custom.
  return hostname.endsWith('.preview.emergentagent.com');
}

export function useDomainContext() {
  const [domainContext, setDomainContext] = useState({
    isCustomDomain: false,
    municipio: null,
    loading: true,
    resolved: false,
  });

  useEffect(() => {
    const hostname = (window.location.hostname || '').toLowerCase();

    if (ALL_MAIN.has(hostname) || isPreviewHost(hostname)) {
      setDomainContext({
        isCustomDomain: false,
        municipio: null,
        loading: false,
        resolved: true,
      });
      return;
    }

    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiBase}/api/domain-resolve?host=${encodeURIComponent(hostname)}`)
      .then((r) => r.json())
      .then((data) => {
        setDomainContext({
          isCustomDomain: Boolean(data && data.found),
          municipio: data && data.found ? data : null,
          loading: false,
          resolved: true,
        });
      })
      .catch(() => {
        setDomainContext({
          isCustomDomain: false,
          municipio: null,
          loading: false,
          resolved: true,
        });
      });
  }, []);

  return domainContext;
}

export default useDomainContext;
